import { CriteriaType, DataMartField, DivisionMappingRow } from '@r1-compass-library/models';
import { SalesforceField } from '@r1-compass-library/models/salesforce/salesforce.model';

export type DivisionBasicSettings = {
    dataMartTable: string;
    dataMartRequiredField: DataMartField;
    allowOverrideQueries: boolean;

    multipleRecommendations?: MultipleRecommendations;

    lifecycleRulePlayRecordIdField: DataMartField;
    lifecycleRuleRecommendationIdField: DataMartField;

    eloquaSiteName?: string;

    allowedCriteria?: CriteriaType[];

    additionalFields?: DefinedField[];
};
export type DivisionSalesforceTargetSettings = {
    salesforcePlayObject: string;
    salesforceRecommendationObject: string;
    salesforceUniqueRecommendationFieldTargetName: string;
    uniqueSalesforceTargetFields: SalesforceUniqueFieldTarget[];
};
export type DivisionDataMappingSettings = {
    fieldMappings: DivisionMappingRow[];
};

export type DivisionMappingSettings = DivisionBasicSettings &
    DivisionSalesforceTargetSettings &
    DivisionDataMappingSettings;

export type SalesforceUniqueFieldTarget = {
    id: string;
    name: string;
    sourceField: DataMartField;
    targetField: SalesforceField;
    divisionTableRelationships: DivisionTableRelationships | undefined;
};

export type DivisionTableRelationships = {
    division: string;
    source: Map<
        `${TableRelationship['sourceTableName']}:${TableRelationship['sourceColumnName']}`,
        TableRelationship[]
    >; //key is in format of TABLE_NAME:COLUMN_NAME, ex T_DS_RSD_MODEL:SF_ACCT_ID
};

export type TableRelationship = {
    sourceTableName: string;
    sourceColumnName: string;
    targets: TargetRelationship[];
};

export type TargetRelationship = {
    targetTableName: string;
    targetColumnName: string;
    functionType: string;
};

export type DefinedField = {
    /**
     * @description Value for form selection
     */
    id: string;
    /**
     * @description Display for form selection only
     */
    name: string;
    source: DefinedFieldSource;
    required: boolean;
    picklistElements?: PicklistElement[];
    value: string | undefined;
    type: DefinedFieldType;
    helperText?: string;
};

export const predefinedFields = [
    { required: true, name: 'Play Id', id: 'playId' },
    { required: true, name: 'Play Internal Name', id: 'playInternalName' },
    { required: true, name: 'Play Salesforce Name', id: 'playSalesforceName' },
    { required: false, name: 'Revenue Targeting', id: 'revenueTargeting' },
    { required: false, name: 'Internal Description', id: 'internalDescription' },
    { required: false, name: 'Salesforce Description', id: 'salesforceDescription' },
    { required: false, name: 'Play Owner Id', id: 'salesforceUserId' },
    { required: false, name: 'Play Start Date', id: 'playInitialStartDate' },
    { required: false, name: 'Play Last Updated Date', id: 'playLastUpdatedDate' },
    { required: false, name: 'Is Play Active', id: 'isPlayActive' },
    { required: false, name: 'Most Recent Start Date', id: 'playMostRecentStartDate' },
    { required: false, name: 'Division Abbreviation', id: 'divisionCode' },
    { required: false, name: 'Talking Points', id: 'talkingPoints' },
    { required: false, name: 'Play Record Id', id: 'playRecordId' },
] as const satisfies Array<{ required: boolean; name: string; id: string }>;

export type PredefinedField = (typeof predefinedFields)[number];

export type PicklistElement = {
    id: string;
    name: string;
    value: string;
};

export type DefinedFieldSource = 'Static' | 'User';
export type DefinedFieldType = 'boolean' | 'varchar' | 'picklist' | 'date';

export type MultipleRecommendations = {
    enabled: boolean;
    maxRecommendations?: number;
    rankingField: string;
    tableMap: MultipleRecommendationTableMap[];
};

export type MultipleRecommendationTableMap = {
    tableName: string;
    fieldName: string;
    modelType: string;
};
